import React, { useMemo, useState, useRef, lazy, Suspense } from 'react';
import { GenerativeBackground } from './GenerativeBackground';
import {
  BG_FX_OVERLAY_LEVELS,
  BG_FX_VIGNETTE_LEVELS,
  BG_FX_FOG_LEVELS,
  BG_FX_NOISE_LEVELS,
  DEFAULT_BG_FX,
  BG_GRAIN_LEVELS,
  BG_COMPENSATION_LEVELS,
  GRADIENT_FOG_LEVELS,
  FOREST_BG_URLS,
  FALLBACK_GRADIENTS,
  isGradientUrl,
  getGradientCss,
  buildGradientCss,
} from '../../constants/backgrounds';

// Lazy-load Three.js primitives — zero cost when not used (code-split)
const Primitives3DBackground = lazy(() => import('./Primitives3DBackground'));

/**
 * Normies hero image background — full-bleed with pixelated rendering.
 * Used when Normies Studio is active. Renders on a solid dark fallback
 * while the image loads, with a smooth opacity transition.
 */
function NormiesHeroBackground({ heroImage }) {
  const [loaded, setLoaded] = useState(false);
  const prevSrc = useRef(null);

  // Reset loaded state when image URL changes
  React.useEffect(() => {
    if (heroImage !== prevSrc.current) {
      prevSrc.current = heroImage;
      setLoaded(false);
    }
  }, [heroImage]);

  return (
    <div className="absolute inset-0" style={{ backgroundColor: '#0a0a0a' }}>
      {heroImage && (
        <img
          src={heroImage}
          alt=""
          className="absolute inset-0 w-full h-full"
          style={{
            objectFit: 'cover',
            imageRendering: 'pixelated',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
          onLoad={() => setLoaded(true)}
          crossOrigin="anonymous"
          draggable={false}
        />
      )}
    </div>
  );
}

/**
 * Forest background component with photo, gradient, generative, 3D primitives, and fallback support.
 *
 * Priority: normies.enabled > primitives3d.enabled > generative.enabled > gradient.enabled > legacy gradient:N URL > image
 *
 * @param {number} props.variant - Background variant index (fallback)
 * @param {string} [props.customUrl] - Custom background URL or legacy "gradient:N"
 * @param {Object} [props.gradient] - Dynamic gradient config from GradientEditor
 * @param {Object} [props.generative] - Generative art config from GenerativeEditor
 * @param {Object} [props.primitives3d] - 3D Primitives config from Primitives3DEditor
 * @param {Object} [props.normies] - Normies Studio config (normies.enabled + normies.heroImage)
 * @param {boolean|Object|string} props.forceFallback - Force fallback gradient (for export)
 * @param {string} [props.bgSolid=null] - Solid hex color (flat background, no image)
 * @param {number} [props.bgGrain=0] - Grain texture level index (0-5, for solid backgrounds)
 * @param {number} [props.bgCompensation=0] - Brightness compensation level index (0-5, darkens to offset grain)
 * @param {number} [props.darken=0] - Extra black overlay opacity
 * @param {Object} [props.bgFx] - Visual effects config { enabled, overlay, vignette, fog, noise }
 */
export function ForestBackground({ variant = 0, customUrl = null, gradient = null, generative = null, primitives3d = null, normies = null, bgSolid = null, bgGrain = 0, bgCompensation = 0, forceFallback = false, darken = 0, bgFx }) {
  // Resolve FX levels — missing bgFx defaults to original hardcoded behavior
  const fx = useMemo(() => {
    const cfg = bgFx || DEFAULT_BG_FX;
    const enabled = cfg.enabled !== false;
    return {
      enabled,
      overlayOpacity: enabled ? (BG_FX_OVERLAY_LEVELS[cfg.overlay ?? DEFAULT_BG_FX.overlay]?.opacity ?? 0.1) : 0,
      vignetteOpacity: enabled ? (BG_FX_VIGNETTE_LEVELS[cfg.vignette ?? DEFAULT_BG_FX.vignette]?.opacity ?? 0.6) : 0,
      fogOpacity: enabled ? (BG_FX_FOG_LEVELS[cfg.fog ?? DEFAULT_BG_FX.fog]?.opacity ?? 0.1) : 0,
      noiseOpacity: enabled ? (BG_FX_NOISE_LEVELS[cfg.noise ?? DEFAULT_BG_FX.noise]?.opacity ?? 0.08) : 0,
    };
  }, [bgFx]);
  const fallbackBg = useMemo(() => {
    return FALLBACK_GRADIENTS[variant % FALLBACK_GRADIENTS.length];
  }, [variant]);

  const [imgOk, setImgOk] = useState(true);
  const [loadedSrc, setLoadedSrc] = useState(null);
  const prevUrlRef = useRef(null);

  // Normies Studio hero image — full-bleed background for Full BG and Polaroid.
  // Before/After and Dashboard render hero images inside the template;
  // their background should be gen art, not the hero.
  const normiesTemplate = normies?.template;
  const isNormiesFullBg = normiesTemplate === 'normies-full-bg' || normiesTemplate === 'normies-polaroid';
  const hasNormies = !!(normies && normies.enabled && normies.heroImage && isNormiesFullBg);
  const normiesHeroImage = hasNormies ? normies.heroImage : null;

  // Dynamic gradient (from GradientEditor) takes priority
  const hasDynamicGradient = !hasNormies && gradient && gradient.enabled;
  const dynamicGradientCss = useMemo(() => {
    if (!hasDynamicGradient) return null;
    return buildGradientCss(gradient);
  }, [hasDynamicGradient, gradient]);

  // 3D Primitive Objects background (from Primitives3DEditor) takes absolute priority
  const hasP3D = !hasNormies && primitives3d && primitives3d.enabled;

  // Generative background (from GenerativeEditor) — suppressed when Normies active
  const hasGenerative = !hasNormies && !hasP3D && generative && generative.enabled;

  // Solid color background (flat hex, no image)
  const hasSolid = !hasNormies && !hasP3D && !hasGenerative && !hasDynamicGradient && !!bgSolid;

  // Legacy gradient:N URL support
  const isLegacyGradient = !hasNormies && !hasDynamicGradient && !hasGenerative && !hasP3D && !hasSolid && isGradientUrl(customUrl);
  const legacyGradientCss = isLegacyGradient ? getGradientCss(customUrl) : null;

  // Any non-image mode active?
  const isGradientMode = hasNormies || hasP3D || hasDynamicGradient || isLegacyGradient || hasGenerative || hasSolid;

  // Image source (only used when not in gradient/solid mode)
  const originalUrl = (!isGradientMode && customUrl) || FOREST_BG_URLS[variant % FOREST_BG_URLS.length];
  
  const base64Cache = typeof forceFallback === 'object' && forceFallback !== null ? forceFallback : null;
  const base64Src = base64Cache ? base64Cache[originalUrl] : null;
  
  React.useEffect(() => {
    setImgOk(true);
  }, [originalUrl, base64Src]);
  
  const useFallbackGradient = 
    isGradientMode ||
    forceFallback === 'fallback' || 
    forceFallback === true ||
    (base64Cache !== null && !base64Src);
  
  const showPhoto = !useFallbackGradient && (imgOk || loadedSrc);
  const imageSrc = base64Src || originalUrl;
  const isNewImage = imageSrc !== loadedSrc;
  if (prevUrlRef.current !== imageSrc) {
    prevUrlRef.current = imageSrc;
  }

  // Choose gradient: dynamic > legacy > fallback
  const activeGradient = dynamicGradientCss || legacyGradientCss || fallbackBg;

  return (
    <div className="absolute inset-0">
      {hasNormies ? (
        /* Normies hero image — full-bleed, pixelated. Highest priority when Normies Studio active. */
        <NormiesHeroBackground heroImage={normiesHeroImage} />
      ) : hasP3D ? (
        /* 3D Primitives background — renders its own scene + CSS gradient bg */
        <Suspense fallback={<div className="absolute inset-0 bg-black" />}>
          <Primitives3DBackground config={primitives3d} />
        </Suspense>
      ) : hasGenerative ? (
        /* Generative noise background — renders its own base + noise SVG */
        <GenerativeBackground config={generative} prefix="canvas" />
      ) : hasSolid ? (
        /* Solid color background — flat hex fill */
        <div className="absolute inset-0" style={{ backgroundColor: bgSolid }} />
      ) : (
        <>
          {/* Gradient layer — always present (fallback or intentional gradient bg) */}
          <div className="absolute inset-0" style={{ backgroundImage: activeGradient }} />

          {showPhoto && (
            <>
              {/* Previous image layer — stays visible while new image loads */}
              {loadedSrc && isNewImage && (
                <img
                  src={loadedSrc}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                  draggable={false}
                />
              )}

              {/* Current image layer */}
              <img
                src={imageSrc}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
                draggable={false}
                onLoad={() => setLoadedSrc(imageSrc)}
                onError={() => { setImgOk(false); setLoadedSrc(null); }}
              />
            </>
          )}
        </>
      )}

      {/* Dark overlay — skip for solid, generative, normies, and p3d backgrounds (they control their own tonality) */}
      {!hasSolid && !hasGenerative && !hasP3D && !hasNormies && fx.overlayOpacity > 0 && (
      <div
        className="absolute inset-0"
        style={{ background: `rgba(0,0,0,${fx.overlayOpacity})` }}
      />
      )}

      {/* User-controlled darken overlay (slider) */}
      {darken > 0 && (
        <div
          className="absolute inset-0"
          style={{ background: `rgba(0,0,0,${darken})` }}
        />
      )}

      {/* Fog effect — controlled by gradient.fog when gradient is active; disabled for solid/generative/normies/primitives3d */}
      {(() => {
        const fogOpacity = (hasGenerative || hasP3D || hasSolid || hasNormies)
          ? 0
          : hasDynamicGradient && gradient.fog !== undefined
            ? (GRADIENT_FOG_LEVELS[gradient.fog] || GRADIENT_FOG_LEVELS[2]).opacity
            : fx.fogOpacity;
        return fogOpacity > 0 ? (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(900px 520px at 50% 70%, rgba(255,255,255,${fogOpacity}), transparent 60%)`,
            }}
          />
        ) : null;
      })()}

      {/* Vignette — skip for solid, generative, and normies backgrounds */}
      {!hasSolid && !hasGenerative && !hasP3D && !hasNormies && fx.vignetteOpacity > 0 && (
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `radial-gradient(circle at 50% 50%, transparent 45%, rgba(0,0,0,${fx.vignetteOpacity}) 100%)`,
        }}
      />
      )}

      {/* Noise texture — default for image/gradient; grain-controlled for solid; skip for generative/normies/primitives3d */}
      {hasSolid ? (
        /* Grain noise for solid backgrounds — normal blend (adds brightness, user compensates via slider) */
        bgGrain > 0 && BG_GRAIN_LEVELS[bgGrain] && (
          <svg
            className="absolute inset-0 h-full w-full"
            style={{ opacity: BG_GRAIN_LEVELS[bgGrain].opacity }}
            aria-hidden="true"
          >
            <filter id="grainFilter">
              <feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="4" stitchTiles="stitch" />
            </filter>
            <rect width="100%" height="100%" filter="url(#grainFilter)" />
          </svg>
        )
      ) : (
        !hasP3D && !hasGenerative && !hasNormies && fx.noiseOpacity > 0 && (
          <svg className="absolute inset-0 h-full w-full" style={{ opacity: fx.noiseOpacity }} aria-hidden="true">
            <filter id="noiseFilter">
              <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" />
            </filter>
            <rect width="100%" height="100%" filter="url(#noiseFilter)" />
          </svg>
        )
      )}

      {/* Brightness compensation — darkens solid bg to offset grain-induced brightening */}
      {hasSolid && bgCompensation > 0 && BG_COMPENSATION_LEVELS[bgCompensation] && (
        <div
          className="absolute inset-0"
          style={{ background: `rgba(0,0,0,${BG_COMPENSATION_LEVELS[bgCompensation].opacity})` }}
        />
      )}
    </div>
  );
}
